import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, interval } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService } from '../../../api/api/authentication.service';
import { IAuthService } from './auth.interface';
import { HttpRequest } from '@angular/common/http';

@Injectable({
    providedIn: 'root'
})
export class StandardAuthService implements IAuthService {
    userToken$: BehaviorSubject<{ [key: string]: any; } | null> = new BehaviorSubject<{ [key: string]: any; } | null>(null);
    userInfo$: BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);
    private tokenFetchSubscription: Subscription;
    private currentTokenExp: number | null = null;

    constructor(
        private authenticationService: AuthenticationService,
    ) {
        this.tokenFetchSubscription = interval(1000 * 60) // Fetch token every 1 min
            .pipe(
                switchMap(() => this.fetchUserToken())
            )
            .subscribe();

        // Initial fetch
        setTimeout(() => {
            this.fetchUserToken().subscribe();
        }, 0);
    }

    fetchUserToken(): Observable<any> {
        let expiresIn = this.currentTokenExp == null ? 0 : this.currentTokenExp - Date.now() / 1000;

        // Only call if token within 5min of expiring
        if (this.currentTokenExp && (expiresIn > 300)) {
            return of(null);
        }

        return this.authenticationService.getAuthToken().pipe(
            tap((response: any) => {
                this.currentTokenExp = response.exp; // eg 1750408504
                let userInfo = {
                    userId: (response || {}).account_id || null,
                    displayName: (response || {}).name || 'Unknown User',
                }
                this.gainsightIdentify(response);
                this.userInfo$.next(userInfo);
            }),
            catchError(error => {
                console.error('Fetch user token failed', error);
                this.userInfo$.next(null);
                return of(null); // Return an observable with a fallback value, if needed
            })
        );
    }

    ngOnDestroy() {
        // Clean up the subscription to avoid memory leaks
        if (this.tokenFetchSubscription) {
            this.tokenFetchSubscription.unsubscribe();
        }
    }

    refreshToken(): Observable<any> {
        return this.authenticationService.refreshToken();
    }

    injectAuth(request: HttpRequest<any>): HttpRequest<any> {
        // credentials handled by backend cookie
        return request.clone({ withCredentials: true })
    }

    getLoginUrl(redirectUrl: string): Observable<string> {
        return this.authenticationService.getLoginUrl(redirectUrl);
    }

    loginWithAuthorizationCode(authCode: string, redirectUrl: string): Observable<any> {
        return this.authenticationService.loginWithAuthorizationCode(authCode, redirectUrl);
    }

    gainsightIdentify(userToken: any): void {
        const aptrinsic = (window as any).aptrinsic;
        if (typeof aptrinsic !== 'function') {
            console.warn('Aptrinsic not loaded, skipping user identification');
            return;
        }

        let auraHost = window?.location?.hostname.indexOf('tech-support') >= 0 ? 'tech-support' : 'default';
        aptrinsic('identify',
            {
                // User Fields
                id: userToken.account_id,
                email: userToken.email || '',
                firstName: userToken.given_name || '',
                lastName: userToken.family_name || '',
            },
            {
                id: "aura-standalone",
                name: auraHost,
            }
        );
    }

}